import { useCallback, useMemo, useState } from 'react'
import { notification } from 'antd'
import isNil from 'lodash/isNil'
import { useRemoveFundingAccessMutation } from '../../api/user-invitation-api-client'
import { extractError } from '../../api/extract-error'
import {
  RemoveFundingAccessAction,
  RemoveFundingAccessResultItem,
  UserListRow,
} from '../../api/dto/user-invitation.dto'
import {
  buildAccessRemovalPlan,
  EMPTY_ACCESS_REMOVAL_PLAN,
} from './accessRemovalBuckets'

interface UseRemoveAccessParams {
  selectedRows: UserListRow[]
  onBulkFinished: (emails: string[]) => void
}

/**
 * Takes funding access away from a selection in full: the activation link, the
 * invoices, the companies and the roles. One confirm step and one report cover
 * every route, since the operator is asking for the same thing either way.
 */
export const useRemoveAccess = ({
  selectedRows,
  onBulkFinished,
}: UseRemoveAccessParams) => {
  const [rows, setRows] = useState<UserListRow[] | null>(null)
  const [results, setResults] = useState<
    RemoveFundingAccessResultItem[] | null
  >(null)
  const [progress, setProgress] = useState<{
    processed: number
    total: number
  } | null>(null)

  const removeMutation = useRemoveFundingAccessMutation({
    onProgress: (processed, total) => setProgress({ processed, total }),
  })

  const plan = useMemo(
    () =>
      isNil(rows) ? EMPTY_ACCESS_REMOVAL_PLAN : buildAccessRemovalPlan(rows),
    [rows]
  )

  const actionableCount = plan.targets.length

  const handleOpenBulk = useCallback(() => {
    if (selectedRows.length === 0) return
    setResults(null)
    setProgress(null)
    setRows(selectedRows)
  }, [selectedRows])

  const handleClose = useCallback(() => {
    if (!isNil(results)) {
      onBulkFinished(
        results
          .filter(
            (result) => result.action === RemoveFundingAccessAction.Failed
          )
          .map((result) => result.email)
      )
    }
    setRows(null)
    setResults(null)
    setProgress(null)
  }, [results, onBulkFinished])

  const handleSubmit = useCallback(
    async (reason: string) => {
      if (actionableCount === 0) return
      setProgress({ processed: 0, total: actionableCount })

      try {
        const { results: removed } = await removeMutation.mutateAsync({
          removals: plan.targets.map(({ row, roles }) => ({
            email: row.email,
            personId: row.personId,
            roleIds: roles.map((role) => role.id),
          })),
          reason,
        })
        setResults(removed)
      } catch (error) {
        notification.error({
          message: extractError(error, 'Failed to remove access'),
        })
      } finally {
        setProgress(null)
      }
    },
    [actionableCount, plan, removeMutation]
  )

  return {
    plan,
    actionableCount,
    modalOpen: !isNil(rows),
    results,
    progress,
    isSubmitting: removeMutation.isPending,
    handleOpenBulk,
    handleClose,
    handleSubmit,
  }
}
